'use client'

import { useMandatoryContext } from '@repo/hooks'
import { createContext, memo, type PropsWithChildren, useContext, useState } from 'react'

export interface DebugOverrides {
  hasLegacyMorpho?: boolean
  isUkUser?: boolean
  /** Force the partner-embed banner on/off without an actual iframe embed. */
  showEmbedderBanner?: boolean
  /** Force the deprecated-vault prompt on without holding a real position. */
  hasDeprecatedVaultPosition?: boolean
}

export interface DebugOverrideContextProps {
  overrides: DebugOverrides
  setOverride: <K extends keyof DebugOverrides>(key: K, value: DebugOverrides[K]) => void
  clearAll: () => void
  isActive: boolean
}

const DebugOverrideContext = createContext<DebugOverrideContextProps | null>(null)

const NOOP_DEBUG_OVERRIDES: DebugOverrideContextProps = {
  overrides: {},
  setOverride: () => {},
  clearAll: () => {},
  isActive: false
}

/**
 * Throws when used outside `DebugOverrideProvider`. Use from components
 * that are guaranteed to live inside the provider (e.g., `DebugPanel`).
 */
export const useDebugOverrides = () => {
  return useMandatoryContext(DebugOverrideContext, 'DebugOverride')
}

/**
 * Safe version that returns empty overrides if used outside the provider.
 * Use this in notification components so they work with or without the debug panel.
 */
export const useDebugOverridesSafe = (): DebugOverrideContextProps => {
  return useContext(DebugOverrideContext) ?? NOOP_DEBUG_OVERRIDES
}

const DebugOverrideProvider = ({ children }: PropsWithChildren) => {
  const [overrides, setOverrides] = useState<DebugOverrides>({})

  const setOverride = <K extends keyof DebugOverrides>(key: K, value: DebugOverrides[K]) => {
    setOverrides(prev => {
      if (value === undefined) {
        const rest = { ...prev }
        delete rest[key]
        return rest
      }
      return { ...prev, [key]: value }
    })
  }

  const clearAll = () => setOverrides({})

  const isActive = Object.values(overrides).some(v => v !== undefined)

  const value = { overrides, setOverride, clearAll, isActive }

  return <DebugOverrideContext.Provider value={value}>{children}</DebugOverrideContext.Provider>
}

export default memo(DebugOverrideProvider)
